<template>
  <div class="">
    <el-form
      class="add-form"
      @submit.prevent="checkTaxGroupInformation"
      @keydown.enter.prevent="() => {}"
      :model="taxGroup"
      label-position="top"
      ref="taxGroupForm"
    >
      <div class="form-group-wrapper">
      <!-- Row 1: Organization -->
          <el-form-item
            v-if="
              showOrganization &&
              $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
            "
          class="form-group-wrapper-item h3 w-100"
            :label="`${$t('COMMON.ORGANIZATION')} (*)`"
            prop="organization.id"
            :rules="[
              {
                required: true,
                message: $t('COMMON.ORGANIZATION_REQUIRED'),
              },
            ]"
          >
            <organization-selector
              :organization="taxGroup.organization.id"
              :filterable="true"
              :showAll="false"
              @organizationChanged="
                (organizationId) => {
                  taxGroup.organization.id = organizationId;
                  onFormChanged();
                }
              "
            />
            <validation-error :errors="apiValidationErrors.organization" />
          </el-form-item>
        <!-- Name -->
        <el-form-item
          v-if="!onlyTaxes"
          class="form-group-wrapper-item h3 w-100"
          :label="`${$t('COMMON.NAME')} (*)`"
          prop="name"
          :rules="[
            {
              required: true,
              message: $t('TAXES.NAME_REQUIRED'),
            },
          ]"
        >
          <el-input
            :placeholder="$t('COMMON.NAME')"
            v-model="taxGroup.name"
            @change="
              () => {
                onFormChanged();
              }
            "
          />
          <validation-error :errors="apiValidationErrors.name" />
        </el-form-item>

        <!-- Country Code -->
        <el-form-item
          v-if="!onlyTaxes"
          class="form-group-wrapper-item h3 w-100"
          :label="`${$t('TAXES.COUNTRY_CODE')} (*)`"
          prop="country_code"
          :rules="[
            {
              required: true,
              message: $t('TAXES.COUNTRY_CODE_REQUIRED'),
            },
          ]"
        >
          <el-input
            :placeholder="$t('TAXES.COUNTRY_CODE')"
            v-model="taxGroup.country_code"
            @change="
              () => {
                onFormChanged();
              }
            "
          />
          <validation-error :errors="apiValidationErrors.country_code" />
        </el-form-item>

        <!-- Excerpt -->
        <el-form-item
          v-if="!onlyTaxes"
          class="form-group-wrapper-item h3 w-100"
          :label="$t('COMMON.EXCERPT')"
          prop="excerpt"
        >
          <el-input
            type="textarea"
            :autosize="{ minRows: 3, maxRows: 4 }"
            :placeholder="$t('COMMON.EXCERPT')"
            v-model="taxGroup.excerpt"
          >
          </el-input>
          <validation-error :errors="apiValidationErrors.excerpt" />
        </el-form-item>

        <!-- Taxes Selection -->
        <el-form-item
          class="taxe-selection h3 w-100 mb-3 form-group-wrapper-item w-100"
          :label="`${$t('TAXES.SELECT_TAXES')} (*)`"
          prop="taxes"
          :rules="[
            {
              required: true,
              message: $t('TAXES.REQUIRED'),
            },
          ]"
        >
          <tax-selector
            with-filters
            :tax="getTaxGroupIds(taxGroup?.taxes)"
            :multiple="true"
            :filterable="true"
            :showAll="false"
            :allowCreate="false"
            @taxChanged="taxSelected"
          />
        </el-form-item>

        <div class="submit-wrapper">
          <base-button
            type="button"
            class="btn btn-sm elite-submit"
            native-type="submit"
            @click.prevent="checkTaxGroupInformation"
            :disabled="loading || processing"
          >
            <i class="fas fa-spinner fa-spin" v-if="loading || processing" />
            {{
              taxGroup.id
                ? $t("TAXES.EDIT_TAX_GROUP")
                : $t("TAXES.ADD_TAX_GROUP")
            }}
          </base-button>
          <button
            v-if="onlyTaxes"
            type="button"
            class="btn btn-sm btn-danger btn-cancel ml-2"
            @click="cancelEdition"
            :disabled="loading || processing"
          >
            {{ $t("COMMON.CANCEL") }}
          </button>
        </div>
      </div>
    </el-form>

    <modal
      :show="showTaxGroupCheckModal"
      modal-classes="modal-secondary"
      size="lg"
    >
      <div class="" slot="header">
        <h2 id="">
          {{ $t("COMMON.CONFIRMATION") }}
        </h2>
      </div>
      <div class="">
        <div class="row">
          <p class="text-lg text-center">
            {{
              $t("TAXES.SAME_TAX_GROUPS", {
                name: taxGroup.name,
              })
            }}
          </p>
        </div>
        <div class="page-wrapper-table-body">
          <div class="page-wrapper-table-body-inner">
            <el-table
              class="align-items-center table-flush"
              header-row-class-name="thead-light"
              :data="taxGroups"
            >
              <div class="table-loading" slot="empty" v-if="loading">
                <img src="/img/loading.gif" alt="" />
              </div>
              <el-table-column
                :label="$t('COMMON.NAME')"
                min-width="250"
                prop="name"
              >
                <template v-slot="{ row }">
                  <span>
                    {{ `${row.name}` }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('COMMON.EXCERPT')" min-width="300">
                <template v-slot="{ row }">
                  {{ row.excerpt ?? "" }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
      <template slot="footer">
        <button
          type="submit"
          class="btn btn-primary"
          @click="continueFormSubmitProcess"
          :disabled="loading || processing"
        >
          <span v-if="loading || processing" class="btn-inner--icon">
            <i class="fas fa-spinner fa-spin"></i>
          </span>
          {{ $t("COMMON.CONTINUE") }}
        </button>

        <button
          type="button"
          class="btn btn-link ml-auto"
          @click="toggleTaxGroupCheckModalState"
          :disabled="loading || processing"
        >
          {{ $t("COMMON.CANCEL") }}
        </button>
      </template>
    </modal>
  </div>
</template>
<script>
import { cloneDeep } from "lodash";
import {
  Option,
  Select,
  Table,
  TableColumn,
  Form,
  FormItem,
} from "element-ui";
import formMixin from "@/mixins/form-mixin";
import ValidationError from "@/components/ValidationError.vue";
import TaxSelector from "../../TaxManagement/components/TaxSelector.vue";
import OrganizationSelector from "@/components/OrganizationSelector.vue";

export default {
  layout: "DashboardLayout",

  components: {
    ValidationError,
    OrganizationSelector,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    TaxSelector,
  },

  mixins: [formMixin],

  props: {
    showOrganization: {
      type: Boolean,
      default: true,
    },
    taxGroupData: {
      type: Object,
      default: () => ({}),
    },
    formErrors: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hideSubmit: {
      type: Boolean,
      default: false,
    },
    onlyTaxes: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let taxGroupData = { ...this.taxGroupData };
    if (!taxGroupData.organization) {
      taxGroupData.organization = { type: "organizations", id: null };
    }
    if (!taxGroupData.relationshipNames) {
      taxGroupData.relationshipNames = ["organization"];
    }
    taxGroupData = this.$fillUserOrganizationData(taxGroupData);
    return {
      taxGroup: taxGroupData,
      taxGroups: [],
      total: 0,
      showModal: false,
      showTaxGroupCheckModal: false,
      processing: false,
    };
  },

  methods: {
    taxSelected(selected) {
      this.taxGroup.taxes = [...selected].map((item) => ({
        type: "taxes",
        id: item,
      }));
    },
    async handleSubmit() {
      const checkUniqueTaxGroup =
        await this.checkTaxGroupUniqueInformation();
      if (checkUniqueTaxGroup) {
        await this.sendInformation();
      } else {
        this.showTaxGroupCheckModal = true;
      }
    },

    async sendInformation() {
      if (this.taxGroup.name) {
        this.taxGroup.name = this.taxGroup.name.toString();
      }
      let taxGroupData = cloneDeep(this.taxGroup);
      taxGroupData = this.$fillUserOrganizationData(taxGroupData);
      this.$emit("taxGroupSubmitted", taxGroupData);
    },

    cancelEdition() {
      this.$emit("cancelEdition");
    },

    getTaxGroupIds(taxes) {
      if (taxes && taxes.length) {
        return taxes.map((tax) => tax.id);
      }
    },

    onFormChanged() {
      this.$emit("formChanged");
    },

    async getList() {
      if (!this.taxGroup?.name) {
        return;
      }
      try {
        this.processing = true;
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...{
              search: `${this.taxGroup?.name}`,
            },
          },
          page: {
            number: 1,
            size: 999,
          },
        };
        await this.$store.dispatch("taxGroups/list", params);
        this.taxGroups = this.$store.getters["taxGroups/list"];
        this.total = this.$store.getters["taxGroups/listTotal"];
        this.processing = false;
      } catch (error) {
        this.processing = false;
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async checkTaxGroupUniqueInformation() {
      await this.getList();
      switch (this.total) {
        case 0:
          return true;
        case 1:
          return !!this.taxGroup?.id;
        default:
          return false;
      }
    },

    async continueFormSubmitProcess() {
      this.processing = true;
      await this.toggleTaxGroupCheckModalState();
      await this.sendInformation();
    },

    async toggleTaxGroupCheckModalState() {
      this.showTaxGroupCheckModal = !this.showTaxGroupCheckModal;
    },

    checkTaxGroupInformation() {
      this.$refs["taxGroupForm"].validate(async (valid) => {
        if (valid) {
          if (this.taxGroup.name) {
            this.handleSubmit();
            this.showModal = false;
          } else {
            this.showModal = true;
          }
        }
      });
    },

    async toggleModalState() {
      this.showModal = !this.showModal;
    },
  },

  watch: {
    formErrors(errors) {
      if (errors) {
        this.setApiValidation(errors);
      }
    },
    taxGroupData(taxGroupData) {
      if (taxGroupData) {
        const cloned = cloneDeep(taxGroupData);
        if (!cloned.organization) {
          cloned.organization = { type: "organizations", id: null };
        }
        if (!cloned.relationshipNames) {
          cloned.relationshipNames = ["taxes", "organization"];
        }
        this.taxGroup = {
          ...this.taxGroup,
          ...cloned,
        };
      }
    },
  },
};
</script>

<style>
.add-form .btn-cancel {
  font-size: 14px;
  min-height: 40px;
  border-radius: 8px;
  padding: 0 15px;
  margin: 0;
  border: none;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;
  color: #fff;
}

.add-form .el-input__inner,
.add-form .el-textarea {
  background-color: transparent;
  border-radius: 8px;
  color: #000;
}
.form-group-wrapper-item .el-select {
  width: 100%;
}
.form-group-wrapper-item label {
  margin-bottom: 0.1rem !important;
}
.form-group-wrapper-item .el-form-item__label {
  padding: 0 0 1px !important;
}
</style>
